/**
 * El registro de quién hizo qué.
 *
 * Existe para responder la pregunta que siempre termina apareciendo —«¿quién
 * anuló esta factura?», «¿quién le cambió el precio a este servicio?»—. Por
 * eso el buscador acepta el número del documento: nadie llega aquí a mirar el
 * registro completo, llega con una factura concreta en la mano.
 *
 * Es solo de consulta. No hay botón para borrar ni editar nada, a propósito.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Chip, Stack, TextField, MenuItem, CircularProgress,
  Alert, Collapse, IconButton, Button, InputAdornment, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api from '../api';
import { useEsMovil, tablaAncha, filtro, ENCABEZADO } from '../estilos/responsivo';
import { TARJETA } from '../estilos/tarjeta';

const ACCIONES = {
  crear:  { label: 'Creó',    bg: '#dcfce7', fg: '#15803d' },
  editar: { label: 'Modificó', bg: '#fef3c7', fg: '#a16207' },
  borrar: { label: 'Borró',   bg: '#fee2e2', fg: '#b91c1c' },
};

const fechaHora = (v) => (v ? new Date(v).toLocaleString('es-CO',
  { day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit' }) : '—');

// Los nombres de columna de la base no se le muestran a nadie tal cual.
const CAMPOS = {
  numero_factura: 'número', tercero_id: 'cliente', sede_id: 'sede',
  precio_unitario: 'precio', impuesto_pct: 'IVA %', total_pagado: 'pagado',
  saldo_pendiente: 'saldo', estado: 'estado', precio: 'precio',
  motivo_anulacion: 'motivo de anulación', cuenta_contable_id: 'cuenta',
  empleado_id: 'quién atiende', vehiculo_id: 'vehículo',
  tipo_orden_id: 'tipo de orden', paso: 'paso', datos: 'datos de la orden',
};
const campo = (c) => CAMPOS[c] || c.replace(/_/g, ' ');

const valor = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (typeof v === 'number') return new Intl.NumberFormat('es-CO').format(v);
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > 60 ? s.slice(0, 60) + '…' : s;
};

function Fila({ r }) {
  const [abierto, setAbierto] = useState(false);
  const a = ACCIONES[r.accion] || { label: r.accion, bg: '#f1f5f9', fg: '#475569' };
  const campos = r.cambios ? Object.keys(r.cambios) : [];
  // En una edición el detalle son los campos que cambiaron; en un alta o una
  // baja, el contenido completo de la fila, que es lo único que permite
  // reconstruir lo que había.
  const esEdicion = r.accion === 'editar';

  return (
    <>
      <TableRow hover sx={{ '& td': { borderBottom: abierto ? 'none' : undefined } }}>
        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fechaHora(r.fecha)}</TableCell>
        <TableCell sx={{ fontSize: 12.5 }}>
          {r.usuario}
          {r.usuario_rol && (
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
              {r.usuario_rol}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip size="small" label={a.label}
            sx={{ bgcolor: a.bg, color: a.fg, height: 20, fontSize: 11, fontWeight: 700 }} />
        </TableCell>
        <TableCell sx={{ fontSize: 12.5 }}>
          {r.etiqueta || r.entidad_nombre}
          <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
            {r.entidad_nombre}{r.entidad_id ? ` · id ${r.entidad_id}` : ''}
          </Typography>
        </TableCell>
        <TableCell sx={{ fontSize: 12 }}>
          {esEdicion && campos.length > 0 && (
            <Typography sx={{ fontSize: 12 }}>
              {campos.slice(0, 3).map(campo).join(', ')}
              {campos.length > 3 ? ` y ${campos.length - 3} más` : ''}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title={r.ip ? `Desde ${r.ip}` : ''}>
            <span>
              <IconButton size="small" disabled={!campos.length}
                onClick={() => setAbierto(!abierto)}>
                {abierto ? <ExpandLessIcon fontSize="small" />
                  : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: abierto ? undefined : 'none' }}>
          <Collapse in={abierto} unmountOnExit>
            <Box sx={{ py: 1.5, pl: { xs: 0, sm: 2 } }}>
              {esEdicion ? (
                <Box sx={{ display: 'grid', gap: 0.5 }}>
                  {campos.map((c) => (
                    <Stack key={c} direction="row" spacing={1} alignItems="baseline"
                      sx={{ fontSize: 12.5, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, minWidth: 140 }}>
                        {campo(c)}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: '#b91c1c',
                                        textDecoration: 'line-through' }}>
                        {valor(r.cambios[c][0])}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>→</Typography>
                      <Typography sx={{ fontSize: 12.5, color: '#15803d', fontWeight: 600 }}>
                        {valor(r.cambios[c][1])}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 0.25,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)',
                                         lg: 'repeat(3, 1fr)' } }}>
                  {campos.filter((c) => r.cambios[c] !== null
                                     && r.cambios[c] !== undefined).map((c) => (
                    <Typography key={c} sx={{ fontSize: 12 }}>
                      <span style={{ color: '#64748b' }}>{campo(c)}: </span>
                      {valor(r.cambios[c])}
                    </Typography>
                  ))}
                </Box>
              )}
              {r.ruta && (
                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
                  {r.ruta}{r.ip ? ` · desde ${r.ip}` : ''}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Auditoria() {
  const esMovil = useEsMovil();
  const [datos, setDatos] = useState({ total: 0, registros: [] });
  const [opciones, setOpciones] = useState({ entidades: [], usuarios: [], acciones: [] });
  const [f, setF] = useState({ accion: '', entidad: '', usuario: '', desde: '', hasta: '' });
  const [buscar, setBuscar] = useState('');
  const [salto, setSalto] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const LIMITE = 100;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/auditoria', {
        params: { ...f, buscar: buscar.trim() || undefined, limite: LIMITE, salto },
      });
      setDatos(data); setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar el registro');
    } finally { setCargando(false); }
  }, [f, buscar, salto]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/auditoria/filtros').then((r) => setOpciones(r.data)).catch(() => {});
  }, []);

  const cambiar = (k) => (e) => { setSalto(0); setF({ ...f, [k]: e.target.value }); };

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Todo lo que se crea, se modifica y se borra queda aquí. Es solo de
            consulta: estos registros no se editan ni se borran.
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 1 }}>
        <Box sx={{ p: TARJETA.relleno, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <TextField size="small" placeholder="Factura, orden, placa, correo…"
            value={buscar} sx={filtro(240)}
            onChange={(e) => { setSalto(0); setBuscar(e.target.value); }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </InputAdornment>) }} />
          <TextField select size="small" label="Acción" value={f.accion}
            onChange={cambiar('accion')} sx={filtro(130)}>
            <MenuItem value="">Todas</MenuItem>
            {opciones.acciones.map((a) => (
              <MenuItem key={a} value={a}>{ACCIONES[a]?.label || a}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Qué" value={f.entidad}
            onChange={cambiar('entidad')} sx={filtro(180)}>
            <MenuItem value="">Todo</MenuItem>
            {opciones.entidades.map((e) => (
              <MenuItem key={e.valor} value={e.valor}>{e.nombre}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Quién" value={f.usuario}
            onChange={cambiar('usuario')} sx={filtro(200)}>
            <MenuItem value="">Todos</MenuItem>
            {opciones.usuarios.map((u) => (
              <MenuItem key={u.email} value={u.email}>{u.nombre || u.email}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" type="date" label="Desde" value={f.desde}
            InputLabelProps={{ shrink: true }} onChange={cambiar('desde')} sx={filtro(150)} />
          <TextField size="small" type="date" label="Hasta" value={f.hasta}
            InputLabelProps={{ shrink: true }} onChange={cambiar('hasta')} sx={filtro(150)} />
        </Box>
      </Card>

      <Card>
        {cargando ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : datos.registros.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No hay movimientos con esos filtros.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" sx={tablaAncha(880)}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Cuándo', 'Quién', 'Qué hizo', 'Sobre qué', 'Campos', ''].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                        textTransform: 'uppercase', color: '#64748b' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datos.registros.map((r) => <Fila key={r.id} r={r} />)}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" alignItems="center" spacing={2}
              sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flexGrow: 1 }}>
                {salto + 1}–{Math.min(salto + LIMITE, datos.total)} de {datos.total}
              </Typography>
              <Button size="small" disabled={salto === 0}
                onClick={() => setSalto(Math.max(0, salto - LIMITE))}>Anteriores</Button>
              <Button size="small" disabled={salto + LIMITE >= datos.total}
                onClick={() => setSalto(salto + LIMITE)}>Siguientes</Button>
            </Stack>
          </>
        )}
      </Card>
      {esMovil && (
        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
          Desliza la tabla de lado para ver todas las columnas.
        </Typography>
      )}
    </Box>
  );
}
